import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  DeleteOutline,
  LinkOff,
  ListAlt,
} from '@mui/icons-material';

import {
  conditionAt,
  IBaukastenGraph,
  ITargetRef,
  nodeAtPath,
  rowKey,
} from './baukastenModel';
import {
  BaukastenSelection,
  EDGE_COLOR,
  EDGE_LABEL,
  kindColor,
  kindIcon,
  rowText,
  targetKey,
} from './BaukastenNodes';
import { ConditionBuilder } from './ConditionBuilder';
import {
  blockTargetsOf,
  conditionSummary,
  ICondition,
  IEditorNode,
  IEditorSection,
  NODE_KIND_LABEL,
  NodePath,
} from './editorModel';
import { FieldLabelResolver } from './fieldPathLabel';
import { NodeEditor } from './NodeEditor';
import { IArnoValidationIssue } from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Eigenschaften des Gewählten (#1245).
 *
 * Bewusst dieselben Formulare wie im Listen-Editor (`NodeEditor`, `ConditionBuilder`): ein
 * Element sieht beim Bearbeiten gleich aus, egal aus welcher Ansicht man kommt, und es gibt
 * keine zweite Stelle, an der eine Regel anders umgesetzt sein könnte.
 */

interface Props {
  selection: BaukastenSelection | null;
  sections: IEditorSection[];
  graph: IBaukastenGraph;
  editable: boolean;
  questions: { questionId: string; prompt: string; answerType: string }[];
  subBlocks: { blockKey: string; name: string }[];
  idLegalTransaction: number | null;
  fieldLabel: FieldLabelResolver;
  issues: IArnoValidationIssue[];
  selfGating: Map<string, string>;
  pendingChecks: { id: string; condition: ICondition }[];
  onSectionChange: (_index: number, _values: Partial<IEditorSection>) => void;
  onNodeChange: (
    _sectionIndex: number,
    _path: NodePath,
    _node: IEditorNode
  ) => void;
  onConditionChange: (
    _target: ITargetRef,
    _condition: ICondition | null
  ) => void;
  onCheckChange: (_id: string, _condition: ICondition) => void;
  onMoveSection: (_index: number, _delta: number) => void;
  onMoveNode: (_sectionIndex: number, _path: NodePath, _delta: number) => void;
  onMoveNodeToSection: (
    _sectionIndex: number,
    _path: NodePath,
    _target: number
  ) => void;
  onDuplicateNode: (_sectionIndex: number, _path: NodePath) => void;
  onDelete: () => void;
  onSelect: (_selection: BaukastenSelection | null) => void;
  onOpenInList: (_index: number) => void;
}

const Kopf: React.FC<{
  title: string;
  subtitle?: string;
  color?: string;
  icon?: React.ReactNode;
}> = ({ title, subtitle, color, icon }) => (
  <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
    {icon && <Box sx={{ color, display: 'flex', fontSize: 22 }}>{icon}</Box>}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant='caption'
          color='text.secondary'
          noWrap
          component='div'
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  </Stack>
);

export const targetLabel = (
  sections: IEditorSection[],
  target: ITargetRef,
  fieldLabel: FieldLabelResolver
): string => {
  const section = sections[target.sectionIndex];
  if (!section) {
    return '';
  }
  const head = `${target.sectionIndex + 1}. ${section.title}`;
  if (target.path === null) {
    return head;
  }
  const node = nodeAtPath(section.nodes, target.path);
  return node ? `${head} › ${rowText(node, fieldLabel)}` : head;
};

const Hilfe: React.FC<{ editable: boolean }> = ({ editable }) => (
  <Box>
    <Kopf title='So funktioniert der Baukasten' />
    <Stack spacing={1.25}>
      <Typography variant='body2'>
        Die Karten sind die Abschnitte der Urkunde — von oben nach unten in der
        Reihenfolge, in der sie im Dokument stehen.
      </Typography>
      {editable ? (
        <>
          <Typography variant='body2'>
            <b>Bauen:</b> Bausteine aus der Leiste links auf eine Karte ziehen
            (die blaue Linie zeigt, wo sie landen) oder auf die freie Fläche —
            dann entsteht ein neuer Abschnitt. Zeilen und Karten lassen sich
            ebenso verschieben.
          </Typography>
          <Typography variant='body2'>
            <b>Entscheiden:</b> Von einer Antwort einer Frage (runder Punkt)
            eine Linie zu einem Abschnitt oder einer Bedingung (eckiger Punkt)
            ziehen. Die Linie <i>ist</i> die Bedingung: der Teil erscheint nur
            bei dieser Antwort. Für Vorgangsdaten (z. B. mehrere Käufer) eine
            »Prüfung« aus der Leiste ziehen.
          </Typography>
          <Typography variant='body2'>
            <b>Bearbeiten:</b> Etwas anklicken — hier erscheinen seine
            Eigenschaften, mit demselben Texteditor wie in der Listenansicht.
            Entf löscht, Strg+Z macht rückgängig.
          </Typography>
        </>
      ) : (
        <Alert severity='info'>
          Diese Version ist veröffentlicht und nur lesbar. Für Änderungen in der
          Übersicht eine neue Version anlegen.
        </Alert>
      )}
      <Divider />
      <Typography variant='caption' color='text.secondary'>
        Linien
      </Typography>
      <Stack direction='row' sx={{ gap: 0.75, flexWrap: 'wrap' }}>
        {(Object.keys(EDGE_COLOR) as (keyof typeof EDGE_COLOR)[]).map(kind => (
          <Chip
            key={kind}
            size='small'
            variant='outlined'
            label={EDGE_LABEL[kind]}
            sx={{ color: EDGE_COLOR[kind], borderColor: EDGE_COLOR[kind] }}
          />
        ))}
        <Chip size='small' variant='outlined' label='- - - vorgezogene Frage' />
      </Stack>
    </Stack>
  </Box>
);

export const BaukastenInspector: React.FC<Props> = ({
  selection,
  sections,
  graph,
  editable,
  questions,
  subBlocks,
  idLegalTransaction,
  fieldLabel,
  issues,
  selfGating,
  pendingChecks,
  onSectionChange,
  onNodeChange,
  onConditionChange,
  onCheckChange,
  onMoveSection,
  onMoveNode,
  onMoveNodeToSection,
  onDuplicateNode,
  onDelete,
  onSelect,
  onOpenInList,
}) => {
  if (!selection) {
    return <Hilfe editable={editable} />;
  }

  if (selection.kind === 'section') {
    const index = selection.sectionIndex;
    const section = sections[index];
    if (!section) {
      return <Hilfe editable={editable} />;
    }
    const own = issues.filter(issue => issue.sectionId === section.sectionId);
    const gating = selfGating.get(targetKey(index, null));
    return (
      <Box>
        <Kopf title={`Abschnitt ${index + 1}`} subtitle={section.title} />
        <Stack spacing={2}>
          <TextField
            size='small'
            label='Titel'
            value={section.title}
            onChange={event =>
              onSectionChange(index, { title: event.target.value })
            }
            disabled={!editable}
            fullWidth
          />
          <TextField
            size='small'
            select
            label='Standardmäßig'
            value={section.enabled ? 'on' : 'off'}
            onChange={event =>
              onSectionChange(index, { enabled: event.target.value === 'on' })
            }
            disabled={!editable}
            helperText={
              section.enabled
                ? undefined
                : 'Erscheint nur, wenn ein anderer Teil ihn als Textbaustein einfügt.'
            }
          >
            <MenuItem value='on'>aktiv</MenuItem>
            <MenuItem value='off'>nicht aktiv</MenuItem>
          </TextField>
          <Box>
            <Typography variant='caption' color='text.secondary'>
              Bedingung für den ganzen Abschnitt — oder eine Linie an den
              eckigen Punkt im Kopf der Karte ziehen
            </Typography>
            <ConditionBuilder
              condition={section.condition}
              onChange={condition =>
                onConditionChange(
                  { sectionIndex: index, path: null },
                  condition
                )
              }
              questions={questions}
              disabled={!editable}
              idLegalTransaction={idLegalTransaction}
            />
          </Box>
          {gating && (
            <Alert severity='warning'>
              Der Abschnitt hängt an der Frage „{gating}", die in ihm selbst
              steht. Sie würde nie gestellt — setzen Sie die Frage in einen
              früheren Abschnitt.
            </Alert>
          )}
          {own.length > 0 && (
            <Alert
              severity={
                own.some(issue => issue.severity === 'ERROR')
                  ? 'error'
                  : 'warning'
              }
            >
              <Box component='ul' sx={{ m: 0, pl: 2 }}>
                {own.slice(0, 10).map((issue, position) => (
                  <li key={`${issue.code}-${position}`}>{issue.message}</li>
                ))}
              </Box>
            </Alert>
          )}
          <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              size='small'
              startIcon={<ArrowUpward />}
              disabled={!editable || index === 0}
              onClick={() => onMoveSection(index, -1)}
            >
              Nach oben
            </Button>
            <Button
              size='small'
              startIcon={<ArrowDownward />}
              disabled={!editable || index === sections.length - 1}
              onClick={() => onMoveSection(index, 1)}
            >
              Nach unten
            </Button>
            <Button
              size='small'
              startIcon={<ListAlt />}
              onClick={() => onOpenInList(index)}
            >
              In der Liste öffnen
            </Button>
            <Button
              size='small'
              color='error'
              startIcon={<DeleteOutline />}
              disabled={!editable}
              onClick={onDelete}
            >
              Löschen
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (selection.kind === 'node') {
    const { sectionIndex, path } = selection;
    const section = sections[sectionIndex];
    const node = section ? nodeAtPath(section.nodes, path) : null;
    if (!section || !node) {
      return <Hilfe editable={editable} />;
    }
    const siblings =
      path.length > 1
        ? (nodeAtPath(section.nodes, path.slice(0, -1))?.nodes ?? [])
        : section.nodes;
    const position = path[path.length - 1];
    const gating = selfGating.get(targetKey(sectionIndex, path));
    return (
      <Box>
        <Kopf
          title={NODE_KIND_LABEL[node.type] ?? node.type}
          subtitle={`Abschnitt ${sectionIndex + 1} · ${section.title}${path.length > 1 ? ' · im Zweig' : ''}`}
          color={kindColor(node.type)}
          icon={kindIcon(node.type)}
        />
        <Stack
          direction='row'
          alignItems='center'
          sx={{ mb: 1.5, gap: 0.5, flexWrap: 'wrap' }}
        >
          <Tooltip title='Nach oben'>
            <span>
              <IconButton
                size='small'
                disabled={!editable || position === 0}
                onClick={() => onMoveNode(sectionIndex, path, -1)}
              >
                <ArrowUpward fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Nach unten'>
            <span>
              <IconButton
                size='small'
                disabled={!editable || position === siblings.length - 1}
                onClick={() => onMoveNode(sectionIndex, path, 1)}
              >
                <ArrowDownward fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Duplizieren'>
            <span>
              <IconButton
                size='small'
                disabled={!editable}
                onClick={() => onDuplicateNode(sectionIndex, path)}
              >
                <ContentCopy fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Löschen (Entf)'>
            <span>
              <IconButton
                size='small'
                color='error'
                disabled={!editable}
                onClick={onDelete}
              >
                <DeleteOutline fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            size='small'
            select
            label='In Abschnitt'
            value={sectionIndex}
            disabled={!editable || path.length > 1}
            onChange={event =>
              onMoveNodeToSection(
                sectionIndex,
                path,
                Number(event.target.value)
              )
            }
            sx={{ ml: 'auto', minWidth: 170 }}
          >
            {sections.map((item, index) => (
              <MenuItem key={item.sectionId} value={index}>
                {index + 1}. {item.title}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {gating && (
          <Alert severity='warning' sx={{ mb: 1.5 }}>
            Hängt an der Frage „{gating}", die in diesem Zweig selbst steht —
            sie würde nie gestellt.
          </Alert>
        )}
        {node.type === 'question' && (
          <Alert severity='info' sx={{ mb: 1.5 }}>
            Die Antworten erscheinen in der Karte als runde Punkte — von dort
            eine Linie zu dem Teil ziehen, der von der Antwort abhängt.
          </Alert>
        )}
        <NodeEditor
          node={node}
          onChange={next => onNodeChange(sectionIndex, path, next)}
          questions={questions}
          blockTargets={blockTargetsOf(sections, section.sectionId, subBlocks)}
          disabled={!editable}
          idLegalTransaction={idLegalTransaction}
        />
      </Box>
    );
  }

  if (selection.kind === 'edge') {
    const target: ITargetRef = {
      sectionIndex: selection.sectionIndex,
      path: selection.path,
    };
    const condition = conditionAt(sections, target);
    const edge = graph.edges.find(
      item =>
        item.targetRef.sectionIndex === target.sectionIndex &&
        rowKey(item.targetRef.path ?? []) === rowKey(target.path ?? []) &&
        (item.targetRef.path === null) === (target.path === null)
    );
    return (
      <Box>
        <Kopf
          title='Bedingung'
          subtitle={targetLabel(sections, target, fieldLabel)}
          color={edge ? EDGE_COLOR[edge.kind] : undefined}
          icon={<LinkOff fontSize='inherit' />}
        />
        <Stack spacing={2}>
          <Typography variant='body2'>
            {condition
              ? `${conditionSummary(condition, questions, fieldLabel)} — dann erscheint dieser Teil.`
              : 'Keine Bedingung — der Teil erscheint immer.'}
          </Typography>
          {edge?.backward && (
            <Alert severity='info'>
              Die Frage steht erst weiter unten. DiNo stellt sie trotzdem
              vorher, sobald dieser Teil an der Reihe ist.
            </Alert>
          )}
          <ConditionBuilder
            condition={condition}
            onChange={next => onConditionChange(target, next)}
            questions={questions}
            disabled={!editable}
            idLegalTransaction={idLegalTransaction}
          />
          <Stack direction='row' spacing={1}>
            <Button
              size='small'
              color='error'
              startIcon={<LinkOff />}
              disabled={!editable || !condition}
              onClick={() => onConditionChange(target, null)}
            >
              Verbindung lösen
            </Button>
            <Button
              size='small'
              onClick={() =>
                onSelect(
                  target.path === null
                    ? { kind: 'section', sectionIndex: target.sectionIndex }
                    : {
                        kind: 'node',
                        sectionIndex: target.sectionIndex,
                        path: target.path,
                      }
                )
              }
            >
              Ziel öffnen
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (selection.kind === 'check') {
    const pending = pendingChecks.find(item => item.id === selection.id);
    const check = graph.checks.find(item => item.id === selection.id);
    const condition = pending?.condition ?? check?.condition ?? null;
    const targets = graph.edges
      .filter(edge => edge.source === selection.id)
      .map(edge => edge.targetRef);
    return (
      <Box>
        <Kopf
          title='Prüfung auf Vorgangsdaten'
          subtitle={
            pending
              ? 'Noch mit nichts verbunden'
              : `${targets.length} Ziel${targets.length === 1 ? '' : 'e'}`
          }
          color={EDGE_COLOR.field}
        />
        <Stack spacing={2}>
          <Typography variant='body2'>
            Prüft einen Wert aus dem Vorgang — zum Beispiel, ob es mehrere
            Käufer gibt. Vom runden Punkt links eine Linie zu jedem Teil ziehen,
            der davon abhängt.
          </Typography>
          <ConditionBuilder
            condition={condition}
            onChange={next => next && onCheckChange(selection.id, next)}
            questions={questions}
            disabled={!editable}
            allowNone={false}
            idLegalTransaction={idLegalTransaction}
          />
          {targets.length > 0 && (
            <Box>
              <Typography variant='caption' color='text.secondary'>
                Steuert
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {targets.map(target => (
                  <Link
                    key={`${target.sectionIndex}-${rowKey(target.path ?? [])}`}
                    component='button'
                    type='button'
                    variant='body2'
                    align='left'
                    onClick={() =>
                      onSelect({
                        kind: 'edge',
                        sectionIndex: target.sectionIndex,
                        path: target.path,
                      })
                    }
                  >
                    {targetLabel(sections, target, fieldLabel)}
                  </Link>
                ))}
              </Stack>
            </Box>
          )}
          <Button
            size='small'
            color='error'
            startIcon={<DeleteOutline />}
            disabled={!editable}
            onClick={onDelete}
            sx={{ alignSelf: 'flex-start' }}
          >
            {targets.length > 1
              ? 'Prüfung an allen Zielen entfernen'
              : 'Prüfung entfernen'}
          </Button>
        </Stack>
      </Box>
    );
  }

  const external = graph.externals.find(
    item => item.questionId === selection.questionId
  );
  const targets = graph.edges
    .filter(edge => edge.source === `x:${selection.questionId}`)
    .map(edge => edge.targetRef);
  return (
    <Box>
      <Kopf title='Frage aus einem Unterbaustein' subtitle={external?.prompt} />
      <Stack spacing={1.5}>
        <Typography variant='body2'>
          Diese Frage wird in einem eingefügten Textbaustein gestellt und dort
          gepflegt. Linien von ihren Antworten lassen sich hier ziehen wie bei
          jeder anderen Frage.
        </Typography>
        {targets.map(target => (
          <Link
            key={`${target.sectionIndex}-${rowKey(target.path ?? [])}`}
            component='button'
            type='button'
            variant='body2'
            align='left'
            onClick={() =>
              onSelect({
                kind: 'edge',
                sectionIndex: target.sectionIndex,
                path: target.path,
              })
            }
          >
            {targetLabel(sections, target, fieldLabel)}
          </Link>
        ))}
      </Stack>
    </Box>
  );
};
